import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ShieldCheck, Monitor, Smartphone, Trash2, LogOut } from "lucide-react";

const fmt = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) +
    ", " + d.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" });
};

// Turn a raw user-agent into a short, human label.
const label = (ua = "") => {
  const browser = /Edg/.test(ua) ? "Edge" : /Chrome/.test(ua) ? "Chrome" : /Firefox/.test(ua) ? "Firefox"
    : /Safari/.test(ua) ? "Safari" : "Browser";
  const os = /Windows/.test(ua) ? "Windows" : /Mac OS X|Macintosh/.test(ua) ? "macOS"
    : /Android/.test(ua) ? "Android" : /iPhone|iPad|iOS/.test(ua) ? "iOS" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${browser} on ${os}` : browser;
};

const isMobile = (ua = "") => /Android|iPhone|iPad|Mobile/.test(ua);

export default function Security() {
  const [devices, setDevices] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/devices");
      setDevices(data);
    } catch {
      setDevices([]);
      toast.error("Couldn't load your devices");
    }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (d) => {
    if (!window.confirm(`Sign out ${label(d.user_agent)}? It'll need a fresh code next time.`)) return;
    setBusyId(d.device_id);
    try {
      await api.delete(`/auth/devices/${d.device_id}`);
      toast.success("Device revoked");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't revoke device");
    } finally {
      setBusyId(null);
    }
  };

  const revokeOthers = async () => {
    if (!window.confirm("Sign out every other device? They'll each need a verification code next time.")) return;
    setBusyId("others");
    try {
      const { data } = await api.post("/auth/devices/revoke-others");
      toast.success(`Signed out ${data.revoked} other device${data.revoked === 1 ? "" : "s"}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't revoke devices");
    } finally {
      setBusyId(null);
    }
  };

  const others = (devices || []).filter((d) => !d.current);

  return (
    <div className="min-h-screen bg-background grain-bg">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-heading text-4xl tracking-tight" style={{ fontWeight: 900 }}>Security</h1>
            <p className="mt-1 text-muted-foreground">Trusted devices skip the 2-step code when you log in.</p>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <h2 className="font-heading text-lg" style={{ fontWeight: 700 }}>Trusted devices</h2>
          {others.length > 0 && (
            <Button variant="outline" size="sm" data-testid="revoke-others-btn" onClick={revokeOthers}
              disabled={busyId === "others"} className="rounded-full border-black/20 text-destructive">
              <LogOut className="mr-1.5 h-4 w-4" /> Sign out all others
            </Button>
          )}
        </div>

        <div className="mt-4 space-y-3" data-testid="devices-list">
          {devices === null ? (
            [...Array(2)].map((_, i) => <Skeleton key={`device-skel-${i}`} className="h-20 rounded-xl" />)
          ) : devices.length === 0 ? (
            <div className="rounded-xl border border-dashed border-black/15 bg-white p-8 text-center text-muted-foreground" data-testid="devices-empty">
              No trusted devices yet. Check "Trust this device" the next time you verify a login.
            </div>
          ) : (
            devices.map((d) => {
              const Icon = isMobile(d.user_agent) ? Smartphone : Monitor;
              return (
                <motion.div
                  key={d.device_id}
                  data-testid={`device-${d.device_id}`}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between gap-4 rounded-xl border border-black/10 bg-white p-4"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-foreground">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold truncate">{label(d.user_agent)}</p>
                        {d.current && (
                          <Badge data-testid="device-current-badge" className="bg-accent/15 text-accent border-none">This device</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {d.location ? <>{d.location} · </> : null}Added {fmt(d.created_at)} · Last used {fmt(d.last_used_at)}
                      </p>
                    </div>
                  </div>
                  {!d.current && (
                    <Button variant="ghost" size="sm" data-testid={`revoke-device-${d.device_id}`}
                      onClick={() => revoke(d)} disabled={busyId === d.device_id}
                      className="shrink-0 text-destructive hover:bg-destructive/10">
                      <Trash2 className="mr-1.5 h-4 w-4" /> Revoke
                    </Button>
                  )}
                </motion.div>
              );
            })
          )}
        </div>
      </main>
    </div>
  );
}
